import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import { callAxios } from '../hooks/useAxios'
import { Package, User } from '../types/models'
import { useApp } from '../hooks/useApp'
import { FileUploadData, FileUploader } from '../components/atoms/FileUploader'
import { TopTitle } from '../components/atoms/TopTitle'
import { t } from 'i18next'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { Text } from '../components/atoms/Text'
import { DateTime } from 'luxon'

export function AdminBillingPage(): React.ReactElement {

  const app = useApp()
  const [packages, setPackages] = useState<Package[]>([])
  const [users, setUsers] = useState<Record<string, User>>({})
  const [failed, setFailed] = useState<string[]>([])
  const [success, setSuccess] = useState<string[]>([])
  const [expires, setExpires] = useState<string>((DateTime.now().year + 1) + '-01-01')

  const updatePackage = async (user: User): Promise<boolean> => {
    const res = await callAxios<User>({
      url: `/users/${user.id}`
    })

    if (res.success) {
      if (res.data.subscriptions) {
        const sub = res.data.subscriptions.find(s => s.package.code === 'BASIC')
        if (sub) {
          const res = await callAxios({
            method: 'PATCH',
            url: `/subscriptions/${sub.id}`
          }, { expires: `${expires} 00:00:00Z` })

          return res.success
        } else {
          const pkg = packages.find(p => p.code === 'BASIC')

          const res = await callAxios({
            method: 'POST',
            url: '/subscriptions'
          }, { package_id: pkg?.id, user_id: user.id, expires: `${expires} 00:00:00Z` })

          return res.success
        }
      }
    }

    return false
  }

  const onUpload = async (files: FileUploadData[]): Promise<void> => {
    const fail: string[] = []
    const ok: string[] = []
    for (const file of files) {
      for (let line of file.data.split('\n')) {
        line = line.trim().toLocaleLowerCase()
        if (line === '') continue
        if (users[line] && await updatePackage(users[line])) {
          ok.push(line)
        } else {
          fail.push(line)
        }
      }
    }
    setFailed(fail)
    setSuccess(ok)
  }

  useEffect(() => {
    callAxios<User[]>({
      url: '/users',
    }).then(res => {
      if (res.success) {
        const users: Record<string, User> = {}
        res.data.forEach((user: User) => {
          users[user.email.toLocaleLowerCase()] = user
        })
        setUsers(users)
      } else {
        app.addError(res)
      }
    })

    callAxios<Package[]>({
      url: '/packages',
    }).then(res => {
      if (res.success) {
        setPackages(res.data)
      } else {
        app.addError(res)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <Box>
    <TopTitle title={t('Billing')}/>
    <PageSubtitle title={t('Subscriptions')}/>
    <Box sx={{ my: 2 }}>
      <Trans>Enter the expire time of the subscription.</Trans><> </>
      <Trans>Upload a list of emails to approve monthly payment to the users in the list.</Trans>
      <Box sx={{ my: 2 }}>
        {success.length > 0 && <Typography color="secondary.main"><Trans>Successful subscriptions:</Trans> {success.length}</Typography>}
      </Box>
      {failed.length > 0 && <Typography color="primary.main"><Trans>The following emails not found:</Trans></Typography>}
      {failed.map((email, idx) => <Box key={idx}>{email}</Box>)}
    </Box>
    <Text size="small" type="date" value={expires} onChange={e => setExpires(e.target.value)}/>&nbsp;&nbsp;&nbsp;
    <FileUploader onUpload={onUpload}/>
  </Box>
}
